import React, { useEffect, useState, useRef } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { APIContact, ContactGroup } from '@super-software-inc/foundation'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { FlexRow, PrimaryButton, TextInput } from 'components/lib'
import { omit } from 'lodash'
import { toastError, toastSuccess } from 'components/lib/Toast'
import FixedBottomButtons from 'components/lib/Forms/FixedBottomButtons'
import { EditorRef } from 'components/app/Editor/types'

import {
  allPhoneRoutingRulesAtom,
  authenticatedUserAtom,
  phoneRoutingRuleFormAtom,
} from '../../../state/atoms'
import {
  createPhoneRoutingRule,
  updatePhoneRoutingRule,
  APIPhoneRoutingRule,
} from '../../../api/assistant/phoneRoutingRule'
import { getContacts } from '../../../api/contacts/getContacts'

import PropertySelector from '../Tasks/PropertySelector'
import Editor from '../Editor/Editor'

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  description: Yup.string().required('Description is required'),
  associationIds: Yup.array()
    .of(Yup.string())
    .required('Association is required'),
})

const PhoneRoutingRuleForm = () => {
  const [phoneRoutingRuleForm, setPhoneRoutingRuleForm] = useRecoilState(
    phoneRoutingRuleFormAtom,
  )
  const [, setAllPhoneRoutingRules] = useRecoilState(allPhoneRoutingRulesAtom)
  const { secrets, selectedContact, selectedCompany } = useRecoilValue(
    authenticatedUserAtom,
  )
  const [associationContacts, setAssociationContacts] = useState<APIContact[]>(
    [],
  )
  const editorRef = useRef<EditorRef>(null)
  const [tempDescription, setTempDescription] = useState('')

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.value =
        phoneRoutingRuleForm.phoneRoutingRule?.description || ''
    }
  }, [phoneRoutingRuleForm.phoneRoutingRule?.description])

  useEffect(() => {
    const fetchContacts = async () => {
      if (!selectedCompany.id) return

      try {
        const contacts = await getContacts(
          secrets.find(s => s.companyId === selectedCompany.id),
          selectedCompany.id,
          selectedCompany.associationIds,
          [
            ContactGroup.Management,
            ContactGroup.Staff,
            ContactGroup.Board,
            ContactGroup.Sponsors,
            ContactGroup.Vendors,
          ],
        )
        setAssociationContacts(contacts)
      } catch (error) {
        toastError('Error fetching contacts')
      }
    }

    fetchContacts()
  }, [selectedCompany, secrets])

  const handleSubmit = async (values: Partial<APIPhoneRoutingRule>) => {
    const updatedPhoneRoutingRule = {
      ...(phoneRoutingRuleForm.phoneRoutingRule || {}),
      ...omit(values, 'id', 'createdAt', 'updatedAt'),
    }

    if (!selectedContact?.companyId) {
      toastError('No company ID found')
      return
    }

    try {
      if (phoneRoutingRuleForm.phoneRoutingRule.id) {
        // Update existing phone routing rule

        const updatedRule = await updatePhoneRoutingRule(
          phoneRoutingRuleForm.phoneRoutingRule.id,
          selectedContact.companyId,
          updatedPhoneRoutingRule,
        )

        setAllPhoneRoutingRules(prevRules =>
          prevRules.map(rule =>
            rule.id === phoneRoutingRuleForm.phoneRoutingRule.id
              ? updatedRule
              : rule,
          ),
        )

        toastSuccess('Phone routing rule updated')
      } else {
        // Create new phone routing rule
        const newRule = await createPhoneRoutingRule(
          selectedContact.companyId,
          updatedPhoneRoutingRule,
        )

        setAllPhoneRoutingRules(prevRules => [...prevRules, newRule])
        toastSuccess('Phone routing rule created')
      }

      setPhoneRoutingRuleForm({
        phoneRoutingRule: {},
        isOpen: false,
      })
    } catch (error: any) {
      toastError(error?.message || 'Error saving phone routing rule')
    }
  }

  const initialValues = {
    title: phoneRoutingRuleForm.phoneRoutingRule?.title || '',
    description: phoneRoutingRuleForm.phoneRoutingRule?.description || '',
    associationIds: phoneRoutingRuleForm.phoneRoutingRule?.associationIds || [],
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, setFieldValue, isSubmitting, values }) => (
        <Form className="bg-white rounded-lg">
          <div className="max-w-[500px] p-6 space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">
              {phoneRoutingRuleForm.phoneRoutingRule.id
                ? 'Edit phone routing rule'
                : 'Add phone routing rule'}
            </h2>

            <div className="space-y-1">
              <label
                htmlFor="title"
                className="block text-sm font-semibold text-gray-900"
              >
                Title
              </label>
              <Field
                id="title"
                name="title"
                as={TextInput}
                type="text"
                placeholder="Add a title"
              />
              {errors.title && touched.title && (
                <div className="text-sm text-red-600">{errors.title}</div>
              )}
            </div>

            <div className="space-y-1">
              <label
                htmlFor="associationIds"
                className="block text-sm font-semibold text-gray-900"
              >
                Accounts
              </label>

              <p className="text-[#627088] text-xs font-normal font-['Inter'] leading-[18px]">
                Leave blank if this rule applies across your company. If it is
                limited to specific properties, select them below.
              </p>

              <PropertySelector
                width="100%"
                value={values.associationIds as string[]}
                onChange={(associationIds: string[]) => {
                  setFieldValue('associationIds', associationIds)
                }}
                allowMultiSelect
                isTaskSheet={false}
                includeCompany={false}
                placeholder="Company-wide"
              />

              {errors.associationIds && touched.associationIds && (
                <div className="text-sm text-red-600">
                  {errors.associationIds}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <label
                htmlFor="description"
                className="block text-sm font-semibold text-gray-900"
              >
                Description
              </label>
              <p className="text-[#627088] text-xs font-normal font-['Inter'] leading-[18px] pb-2">
                Specify the routing rules for incoming phone calls. Include any
                necessary details such as routing conditions, phone numbers to
                forward to, and handling instructions.
              </p>

              <div
                style={{
                  border: '1px solid #E0E0E0',
                  borderRadius: '4px',
                  padding: '0 10px',
                  minHeight: '200px',
                }}
                onBlur={async () => {
                  if (tempDescription === values.description) {
                    return
                  }
                  setFieldValue('description', tempDescription)
                }}
              >
                <Editor
                  ref={editorRef}
                  placeholder="Specify your phone routing rules"
                  associationId={undefined}
                  contacts={associationContacts}
                  onChange={val => {
                    setTempDescription(val)
                  }}
                  forceGroups
                />
              </div>
              {errors.description && touched.description && (
                <div className="text-sm text-red-600">{errors.description}</div>
              )}
            </div>
          </div>

          <FixedBottomButtons>
            <FlexRow style={{ gap: 10 }} justify="flex-end">
              <PrimaryButton
                type="button"
                light
                onClick={() => {
                  setPhoneRoutingRuleForm({
                    phoneRoutingRule: {},
                    isOpen: false,
                  })
                }}
              >
                Cancel
              </PrimaryButton>
              <PrimaryButton type="submit" disabled={isSubmitting}>
                {phoneRoutingRuleForm.phoneRoutingRule.id ? 'Update' : 'Save'}
              </PrimaryButton>
            </FlexRow>
          </FixedBottomButtons>
        </Form>
      )}
    </Formik>
  )
}

export default PhoneRoutingRuleForm
